import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { DevicesState, IDevice, SignalData } from '../types';
import { ApiService } from '../services/api';
import { RootState } from './index';

// Type for signal map
type SignalMap = { [gatewayMac: string]: SignalData };

// Helper to parse timestamp safely
const parseTs = (ts?: string) => (ts ? Date.parse(ts) : 0);

// Helper to merge signal maps, keeping the newest sample per gateway
const mergeSignals = (existing?: SignalMap, incoming?: SignalMap): SignalMap => {
    const out: SignalMap = { ...(existing || {}) };
    if (incoming) {
        for (const [gw, sig] of Object.entries(incoming)) {
            const prev = out[gw];
            if (!prev || parseTs(sig.received_at) > parseTs(prev.received_at)) {
                out[gw] = sig;
            }
        }
    }
    return out;
};

// Helper function to create ApiService with current token
const createApiService = (getState: () => RootState) => {
    const state = getState();
    const apiService = new ApiService(
        process.env.REACT_APP_API_URL || 'http://localhost:3051',
        process.env.REACT_APP_API_URL || 'http://localhost:3051/api'
    );
    // Use token from Redux state, or fallback to localStorage if state hasn't been hydrated yet
    const token = state.auth.token || localStorage.getItem('token');
    if (token) {
        apiService.setToken(token);
    }
    return apiService;
};

interface DevicesStateWithConfig extends DevicesState {
    configured: { [mac: string]: IDevice };
    error: string | null;
}

const initialState: DevicesStateWithConfig = {
    entities: {},
    configured: {},
    available: [],
    monitored: [],
    loading: false,
    error: null,
    monitoredLoaded: false,
};

// Async thunks
export const fetchConfiguredDevices = createAsyncThunk(
    'devices/fetchConfigured',
    async (_, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.fetchConfiguredDevices();
    }
);

export const saveDevice = createAsyncThunk(
    'devices/saveDevice',
    async (device: IDevice, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        if (device._id) {
            return await apiService.updateDevice(device.mac, device);
        } else {
            const { _id, createdAt, updatedAt, ...deviceData } = device;
            return await apiService.createDevice(deviceData);
        }
    }
);

export const deleteDevice = createAsyncThunk(
    'devices/deleteDevice',
    async (mac: string, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        await apiService.deleteDevice(mac);
        return mac;
    }
);

// Hybrid system thunks
export const fetchAvailableDevices = createAsyncThunk(
    'devices/fetchAvailable',
    async (_, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.fetchAvailableDevices();
    }
);

export const fetchMonitoredDevices = createAsyncThunk(
    'devices/fetchMonitored',
    async (options: { freshSignals?: boolean } | undefined, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.fetchMonitoredDevices(options);
    }
);

export const addDeviceToMonitoring = createAsyncThunk(
    'devices/addToMonitoring',
    async ({ mac, settings }: { mac: string; settings: { displayName?: string; color?: string; icon?: string; showTrail?: boolean } }, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.addDeviceToMonitoring(mac, settings);
    }
);

export const removeDeviceFromMonitoring = createAsyncThunk(
    'devices/removeFromMonitoring',
    async (mac: string, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.removeDeviceFromMonitoring(mac);
    }
);

const devicesSlice = createSlice({
    name: 'devices',
    initialState,
    reducers: {
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        updateDevices: (state, action: PayloadAction<{ [mac: string]: IDevice }>) => {
            state.entities = { ...state.entities, ...action.payload };
            state.loading = false;
        },
        updateDevice: (state, action: PayloadAction<IDevice>) => {
            state.entities[action.payload.mac] = action.payload;
        },
        clearDevices: (state) => {
            state.entities = {};
        },
        resetDevicesState: (state) => {
            Object.assign(state, initialState);
        },
        recalculateDevicePositions: (state, action: PayloadAction<{ [mac: string]: IDevice }>) => {
            // Update device positions while preserving other device data
            Object.entries(action.payload).forEach(([mac, updatedDevice]) => {
                if (state.entities[mac]) {
                    state.entities[mac] = {
                        ...state.entities[mac],
                        position: updatedDevice.position,
                        strongestGateway: updatedDevice.strongestGateway,
                        rssi: updatedDevice.rssi
                    };
                }
            });
        },
        updateDevicesFromApi: (state, action: PayloadAction<{ devices: { [mac: string]: IDevice }, gateways: any }>) => {
            // Update device positions from real-time API data
            const { devices } = action.payload;
            Object.entries(devices).forEach(([mac, device]) => {
                const prev = state.entities[mac] || state.configured[mac] || ({} as IDevice);
                const incomingSignals =
                    (device as any).signal as SignalMap | undefined
                    ?? (device as any).lastSignalData as SignalMap | undefined;

                const mergedSignals = mergeSignals(
                    (prev.signal as SignalMap) || (prev.lastSignalData as SignalMap),
                    incomingSignals
                );

                // console.log(`Merging signals for device ${mac}:`, {
                //     previous: prev.signal,
                //     incoming: incomingSignals,
                //     merged: mergedSignals
                // });

                state.entities[mac] = {
                    ...prev,
                    ...device,
                    signal: mergedSignals,
                    lastSignalData: mergedSignals
                };
            });
        },
        updateDeviceFromSSE: (state, action: PayloadAction<{
            mac: string;
            name: string;
            signal: any;
            lastSeen: string;
            timestamp: string;
            position?: { x: number; y: number };
            rssi?: number;
            strongestGateway?: string;
            triangulationInfo?: {
                method: 'trilateration' | 'weighted_centroid' | 'single_gateway';
                confidence: number;
                signalsUsed: number;
                primaryGateway?: string;
                lastCalculated: string;
            };
        }>) => {
            // Update device from Server-Sent Events with triangulation data
            const { mac, name, signal, lastSeen, position, rssi, strongestGateway, triangulationInfo } = action.payload;
            if (state.entities[mac]) {
                const prev = state.entities[mac];
                const prevSignals = (prev?.signal as SignalMap) || (prev?.lastSignalData as SignalMap);
                const incomingSignals = signal as SignalMap | undefined;
                const merged = mergeSignals(prevSignals, incomingSignals);

                state.entities[mac] = {
                    ...prev,
                    name: name || prev.name,
                    lastSeen,
                    // Store merged signal data for SignalStrengthIndicator component
                    signal: merged,
                    lastSignalData: merged,
                    // Update position data if provided by backend
                    ...(position && { position }),
                    ...(rssi !== undefined && { rssi }),
                    ...(strongestGateway && { strongestGateway }),
                    // Add triangulation info from backend
                    ...(triangulationInfo && { triangulationInfo }),
                    updatedAt: new Date().toISOString()
                };
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch configured devices
            .addCase(fetchConfiguredDevices.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchConfiguredDevices.fulfilled, (state, action) => {
                state.loading = false;
                state.configured = {};
                action.payload.forEach((device: IDevice) => {
                    state.configured[device.mac] = device;
                });
            })
            .addCase(fetchConfiguredDevices.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch devices';
            })
            // Save device
            .addCase(saveDevice.fulfilled, (state, action) => {
                state.configured[action.payload.mac] = action.payload;
                if (state.entities[action.payload.mac]) {
                    state.entities[action.payload.mac] = {
                        ...state.entities[action.payload.mac],
                        name: action.payload.name,
                    };
                }
            })
            // Delete device
            .addCase(deleteDevice.fulfilled, (state, action) => {
                delete state.configured[action.payload];
                delete state.entities[action.payload];
            })
            // Hybrid system cases
            .addCase(fetchAvailableDevices.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAvailableDevices.fulfilled, (state, action) => {
                state.loading = false;
                state.available = action.payload.devices || [];
            })
            .addCase(fetchAvailableDevices.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch available devices';
            })
            .addCase(fetchMonitoredDevices.fulfilled, (state, action) => {
                state.monitored = action.payload.devices || [];
                state.monitoredLoaded = true;
            })
            .addCase(addDeviceToMonitoring.fulfilled, (state, action) => {
                // Move device from available to monitored
                state.available = state.available.filter(d => d.mac !== action.payload.device.mac);
                const existingIndex = state.monitored.findIndex(d => d.mac === action.payload.device.mac);
                if (existingIndex >= 0) {
                    state.monitored[existingIndex] = action.payload.device;
                } else {
                    state.monitored.push(action.payload.device);
                }

                // Immediately sync to entities for map display
                const device = action.payload.device;
                state.entities[device.mac] = {
                    ...device,
                    // Apply custom settings to display properties
                    name: device.customSettings?.displayName || device.name || device.mac,
                    // Initially no position - will be updated by SSE or API polling
                    position: undefined,
                };
            })
            .addCase(removeDeviceFromMonitoring.fulfilled, (state, action) => {
                // Move device from monitored to available if still in API
                state.monitored = state.monitored.filter(d => d.mac !== action.payload.device.mac);
                if (action.payload.device.isAvailableInAPI) {
                    const existingIndex = state.available.findIndex(d => d.mac === action.payload.device.mac);
                    if (existingIndex >= 0) {
                        state.available[existingIndex] = action.payload.device;
                    } else {
                        state.available.push(action.payload.device);
                    }
                }

                // Remove from entities so it no longer appears on the map
                delete state.entities[action.payload.device.mac];
            });
    },
});

export const { setLoading, updateDevices, updateDevice, clearDevices, resetDevicesState, recalculateDevicePositions, updateDevicesFromApi, updateDeviceFromSSE } = devicesSlice.actions;
export default devicesSlice.reducer;
